import {
  AlertTriangle,
  CheckCircle2,
  Calculator,
  KeyRound,
  Loader2,
  Megaphone,
  RefreshCw,
  RotateCcw,
  Settings,
  SlidersHorizontal,
  Truck,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ModalDropiIntegration from "../../../../components/integrations/Modals/ModalDropiIntegration";
import { useAuth } from "../../../../context/AuthContext";
import { postDropiIntegration } from "../../../../services/integrations/integrationsService";
import { dictionary, type Language } from "../../../../utils/dictionary";
import type { Period } from "../funnel-detail/types";
import { useFinanceStore } from "../../store/financeStore";
import {
  financeService,
  getFinanceLogisticsOverview,
  getFinanceMetaConnection,
  getFinanceMetaOAuthUrl,
  type FinanceMetaAdAccount,
  type FinanceLogisticsOverview,
  type FinanceMetaConnectionResponse,
} from "../../services/financeService";
import { openMetaOAuthPopup } from "../../services/metaOAuthPopup";

type MetaAccountScope = "all" | "custom";

type FinanceConnectionControlsProps = {
  period: Period;
};

const getDropiIntegrationErrorMessage = (error: unknown, language: Language) => {
  const data = (error as { response?: { data?: { trigger?: string; message?: string } } })?.response?.data;
  const trigger = data?.trigger;
  if (trigger && dictionary[trigger]?.[language]) return dictionary[trigger][language];
  return data?.message || dictionary["integration-error"]?.[language] || "No pudimos conectar Dropi";
};

export function FinanceConnectionControls({ period }: FinanceConnectionControlsProps) {
  const { language } = useAuth();
  const navigate = useNavigate();
  const previewOnboarding = useFinanceStore((state) => state.previewOnboarding);
  const [metaConnection, setMetaConnection] = useState<FinanceMetaConnectionResponse | null>(null);
  const [logistics, setLogistics] = useState<FinanceLogisticsOverview | null>(null);
  const [accounts, setAccounts] = useState<FinanceMetaAdAccount[]>([]);
  const [metaAccountScope, setMetaAccountScope] = useState<MetaAccountScope>("all");
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [syncingMeta, setSyncingMeta] = useState(false);
  const [savingMetaAccount, setSavingMetaAccount] = useState(false);
  const [syncingDropi, setSyncingDropi] = useState(false);
  const [reconfiguringMeta, setReconfiguringMeta] = useState(false);
  const [modalOpenDropi, setModalOpenDropi] = useState(false);
  const [token, setToken] = useState("");
  const popupIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [nextMetaConnection, nextLogistics] = await Promise.all([
        getFinanceMetaConnection().catch(() => null),
        getFinanceLogisticsOverview(period).catch(() => null),
      ]);
      setMetaConnection(nextMetaConnection);
      setLogistics(nextLogistics);
      if (nextMetaConnection?.connected) {
        const accountList = await financeService
          .getFinanceMetaAdAccounts(nextMetaConnection.business_id ?? undefined)
          .catch(() => []);
        const persistedAccountId = nextMetaConnection.selected_ad_account_id ?? null;
        setAccounts(accountList);
        setSelectedAccountIds((currentIds) => {
          const filteredIds = currentIds.filter((accountId) =>
            accountList.some((account) => account.id === accountId)
          );
          if (filteredIds.length > 0) return filteredIds;
          return persistedAccountId && accountList.some((account) => account.id === persistedAccountId)
            ? [persistedAccountId]
            : [];
        });
        setMetaAccountScope((currentScope) => (currentScope === "custom" ? "custom" : "all"));
      } else {
        setAccounts([]);
        setSelectedAccountIds([]);
        setMetaAccountScope("all");
      }
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadStatus();
    return () => {
      if (popupIntervalRef.current) clearInterval(popupIntervalRef.current);
    };
  }, [loadStatus]);

  const handleMetaSync = async () => {
    setSyncingMeta(true);
    try {
      const response = await financeService.startMetaBackfill();
      await loadStatus();
      toast.success(response.message ?? "Meta Ads actualizado");
    } catch {
      toast.error("No pudimos actualizar Meta Ads");
    } finally {
      setSyncingMeta(false);
    }
  };

  const handleSaveMetaAccount = async () => {
    if (metaAccountScope !== "custom" || selectedAccountIds.length !== 1) return;
    const selectedAccountId = selectedAccountIds[0];
    const selectedAccount = accounts.find((account) => account.id === selectedAccountId) ?? null;

    setSavingMetaAccount(true);
    try {
      const updatedConnection = await financeService.updateFinanceMetaAdAccount(
        selectedAccountId,
        resolveAccountName(selectedAccount)
      );
      setMetaConnection(updatedConnection);
      toast.success("Cuenta publicitaria guardada");
    } catch {
      toast.error("No pudimos guardar la cuenta publicitaria");
    } finally {
      setSavingMetaAccount(false);
    }
  };

  const handleMetaReconfigure = async () => {
    if (reconfiguringMeta) return;
    setReconfiguringMeta(true);

    try {
      const response = await getFinanceMetaOAuthUrl();
      if (!response.authorizationUrl) {
        toast.error("No pudimos abrir Meta");
        setReconfiguringMeta(false);
        return;
      }

      const popup = openMetaOAuthPopup(response.authorizationUrl, "finance-meta-oauth", {
        width: 620,
        height: 760,
      });

      if (!popup) {
        window.location.href = response.authorizationUrl;
        return;
      }

      popupIntervalRef.current = setInterval(async () => {
        if (!popup.closed) return;
        if (popupIntervalRef.current) clearInterval(popupIntervalRef.current);
        popupIntervalRef.current = null;
        await loadStatus();
        setReconfiguringMeta(false);
        toast.success("Meta Ads reconfigurado");
      }, 500);
    } catch {
      setReconfiguringMeta(false);
      toast.error("No pudimos iniciar la reconfiguración");
    }
  };

  const handleConnectDropi = async (dropiToken: string) => {
    const cleanToken = dropiToken.trim();
    if (!cleanToken) return;

    try {
      await postDropiIntegration({ integration_token: cleanToken, is_active: true });
      setModalOpenDropi(false);
      setToken("");
      await loadStatus();
      toast.success("Dropi conectado");
    } catch (requestError) {
      toast.error(getDropiIntegrationErrorMessage(requestError, language), { duration: 6000 });
    }
  };

  const handleDropiSync = async () => {
    setSyncingDropi(true);
    try {
      const result = await financeService.startDropiBackfill(period);
      if (!result.ok) throw new Error(result.message || "No pudimos actualizar Dropi");
      await loadStatus();
      toast.success(result.message || "Dropi actualizado");
    } catch (requestError) {
      toast.error(requestError instanceof Error ? requestError.message : "No pudimos actualizar Dropi");
    } finally {
      setSyncingDropi(false);
    }
  };

  const metaConnected = Boolean(metaConnection?.connected);
  const dropiConnected = Boolean(logistics?.dropi_connected);
  const allConnected = metaConnected && dropiConnected;
  const handleRestartOnboarding = () => {
    previewOnboarding();
    setConfigOpen(false);
    navigate("/finance?onboarding=finance");
  };
  const handleConfigureFixedCosts = () => {
    previewOnboarding();
    setConfigOpen(false);
    navigate("/finance?onboarding=finance&step=fixed-costs");
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ConfigurationButton
        connected={allConnected}
        loading={loading}
        onClick={() => {
          setMetaAccountScope("all");
          setConfigOpen(true);
        }}
      />

      {configOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/20 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Configuración de conexiones</h2>
                <p className="mt-1 text-sm text-gray-500">Administra pauta, ventas, órdenes y sincronización.</p>
              </div>
              <button
                type="button"
                onClick={() => setConfigOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition hover:bg-gray-50"
                aria-label="Cerrar configuración"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="divide-y divide-gray-100">
              <ProviderPanel
                connected={metaConnected}
                icon={<Megaphone className="h-5 w-5" />}
                title="Meta Ads"
                description={
                  metaConnected
                    ? `${metaConnection?.business_name ?? "La cuenta"} está lista para leer campañas, pauta y métricas.`
                    : "Conecta Meta Ads para leer campañas y pauta."
                }
                primaryLabel={metaConnected ? "Reconfigurar" : "Conectar Meta"}
                primaryIcon={reconfiguringMeta ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />}
                primaryDisabled={reconfiguringMeta}
                onPrimary={handleMetaReconfigure}
                secondaryLabel="Sincronizar"
                secondaryIcon={syncingMeta ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                secondaryDisabled={!metaConnected || syncingMeta}
                onSecondary={handleMetaSync}
                headerControls={
                  metaConnected ? (
                    <MetaAccountScopeToggle
                      accounts={accounts}
                      connection={metaConnection}
                      accountScope={metaAccountScope}
                      selectedAccountIds={selectedAccountIds}
                      onAccountScopeChange={setMetaAccountScope}
                      onSelectedAccountIdsChange={setSelectedAccountIds}
                    />
                  ) : null
                }
              >
                {metaConnected ? (
                  <MetaAccountList
                    accounts={accounts}
                    connection={metaConnection}
                    accountScope={metaAccountScope}
                    selectedAccountIds={selectedAccountIds}
                    onSelectedAccountIdsChange={setSelectedAccountIds}
                    onSaveAccount={handleSaveMetaAccount}
                    saving={savingMetaAccount}
                    syncing={syncingMeta}
                  />
                ) : null}
              </ProviderPanel>

              <ProviderPanel
                connected={dropiConnected}
                icon={<Truck className="h-5 w-5" />}
                title="Dropi"
                description={
                  dropiConnected
                    ? "Las órdenes y costos están disponibles para Finance."
                    : "Conecta Dropi para leer pedidos, guías y costos."
                }
                primaryLabel={dropiConnected ? "Reconfigurar token" : "Conectar Dropi"}
                primaryIcon={<KeyRound className="h-4 w-4" />}
                onPrimary={() => {
                  setConfigOpen(false);
                  setModalOpenDropi(true);
                }}
                secondaryLabel="Sincronizar"
                secondaryIcon={syncingDropi ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                secondaryDisabled={!dropiConnected || syncingDropi}
                onSecondary={handleDropiSync}
                accent="orange"
              />
            </div>

            <div className="border-t border-gray-100 p-5">
              <button
                type="button"
                onClick={handleConfigureFixedCosts}
                className="mb-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-blue-100 bg-blue-50 text-sm font-bold text-[#2b5ce6] transition hover:bg-blue-100"
              >
                <Calculator className="h-4 w-4" />
                Configurar costos fijos
              </button>
              <button
                type="button"
                onClick={handleRestartOnboarding}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-blue-100 bg-blue-50 text-sm font-bold text-[#2b5ce6] transition hover:bg-blue-100"
              >
                <RotateCcw className="h-4 w-4" />
                Reiniciar onboarding
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {modalOpenDropi ? (
        <ModalDropiIntegration
          setModalOpenDropi={setModalOpenDropi}
          handlePostDropiIntegration={handleConnectDropi}
          token={token}
          setToken={setToken}
          language={language}
        />
      ) : null}
    </div>
  );
}

function ConfigurationButton({
  connected,
  loading,
  onClick,
}: {
  connected: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition hover:bg-slate-50"
      title={`Conexiones: ${connected ? "listas" : "pendientes"}`}
      aria-label={`Configurar conexiones: ${connected ? "listas" : "pendientes"}`}
    >
      <SlidersHorizontal className="h-5 w-5 text-slate-600" />
      <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-white bg-white shadow-sm">
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
        ) : connected ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
        ) : (
          <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
        )}
      </span>
    </button>
  );
}

function ProviderPanel({
  connected,
  icon,
  title,
  description,
  primaryLabel,
  primaryIcon,
  primaryDisabled,
  onPrimary,
  secondaryLabel,
  secondaryIcon,
  secondaryDisabled,
  onSecondary,
  accent = "blue",
  headerControls,
  children,
}: {
  connected: boolean;
  icon: ReactNode;
  title: string;
  description: string;
  primaryLabel: string;
  primaryIcon: ReactNode;
  primaryDisabled?: boolean;
  onPrimary: () => void;
  secondaryLabel: string;
  secondaryIcon: ReactNode;
  secondaryDisabled?: boolean;
  onSecondary: () => void;
  accent?: "blue" | "orange";
  headerControls?: ReactNode;
  children?: ReactNode;
}) {
  const primaryClasses =
    accent === "orange"
      ? "bg-[#F38244] hover:bg-[#e87537]"
      : "bg-[#2b5ce6] hover:bg-[#214ec7]";

  return (
    <div className="p-5">
      <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
              accent === "orange" ? "bg-orange-50 text-[#F38244]" : "bg-blue-50 text-[#2b5ce6]"
            }`}>
              {icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-bold text-gray-900">{title}</h3>
                <span className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-bold ${
                  connected
                    ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                    : "border-gray-200 bg-white text-gray-500"
                }`}>
                  {connected ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                  {connected ? "Conectado" : "Pendiente"}
                </span>
              </div>
              <p className="mt-1 text-sm leading-6 text-gray-500">{description}</p>
            </div>
          </div>
          {headerControls ? <div className="shrink-0 self-start lg:self-start">{headerControls}</div> : null}
        </div>
      </div>

      {children}

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onSecondary}
          disabled={secondaryDisabled}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {secondaryIcon}
          {secondaryLabel}
        </button>
        <button
          type="button"
          onClick={onPrimary}
          disabled={primaryDisabled}
          className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${primaryClasses}`}
        >
          {primaryIcon}
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}

function MetaAccountScopeToggle({
  accounts,
  connection,
  accountScope,
  selectedAccountIds,
  onAccountScopeChange,
  onSelectedAccountIdsChange,
}: {
  accounts: FinanceMetaAdAccount[];
  connection: FinanceMetaConnectionResponse | null;
  accountScope: MetaAccountScope;
  selectedAccountIds: string[];
  onAccountScopeChange: (value: MetaAccountScope) => void;
  onSelectedAccountIdsChange: (value: string[]) => void;
}) {
  const usingAllAccounts = accountScope === "all";

  const selectAllAccounts = () => {
    onAccountScopeChange("all");
  };

  const selectCustomAccounts = () => {
    onAccountScopeChange("custom");
    if (selectedAccountIds.length > 0 || accounts.length === 0) return;
    const persistedAccount = accounts.find((account) => account.id === connection?.selected_ad_account_id);
    onSelectedAccountIdsChange([persistedAccount?.id ?? accounts[0].id]);
  };

  return (
    <div className="inline-flex w-full rounded-lg bg-gray-200 p-0.5 sm:w-auto">
      <button
        type="button"
        onClick={selectAllAccounts}
        className={`inline-flex h-7 flex-1 items-center justify-center whitespace-nowrap rounded-md px-2.5 text-xs font-semibold transition focus:outline-none sm:flex-none ${
          usingAllAccounts ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
        }`}
      >
        Todas
      </button>
      <button
        type="button"
        onClick={selectCustomAccounts}
        className={`inline-flex h-7 flex-1 items-center justify-center whitespace-nowrap rounded-md px-2.5 text-xs font-semibold transition focus:outline-none sm:flex-none ${
          !usingAllAccounts ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
        }`}
      >
        Personalizado
      </button>
    </div>
  );
}

function MetaAccountList({
  accounts,
  connection,
  accountScope,
  selectedAccountIds,
  onSelectedAccountIdsChange,
  onSaveAccount,
  saving,
  syncing,
}: {
  accounts: FinanceMetaAdAccount[];
  connection: FinanceMetaConnectionResponse | null;
  accountScope: MetaAccountScope;
  selectedAccountIds: string[];
  onSelectedAccountIdsChange: (value: string[]) => void;
  onSaveAccount: () => void;
  saving: boolean;
  syncing: boolean;
}) {
  if (accountScope === "all") return null;

  const selectedAccountPersisted =
    selectedAccountIds.length === 1 &&
    Boolean(connection?.selected_ad_account_id) &&
    connection?.selected_ad_account_id === selectedAccountIds[0];

  const toggleAccount = (accountId: string) => {
    const next = selectedAccountIds.includes(accountId)
      ? selectedAccountIds.filter((id) => id !== accountId)
      : [...selectedAccountIds, accountId];
    onSelectedAccountIdsChange(next);
  };

  return (
    <div className="mt-3">
      <div className="max-h-44 overflow-auto rounded-lg border border-gray-200 bg-white p-1">
        {accounts.length > 0 ? (
          accounts.map((account) => {
            const checked = selectedAccountIds.includes(account.id);
            return (
              <label
                key={account.id}
                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleAccount(account.id)}
                  className="h-4 w-4 rounded border-gray-300 text-[#2b5ce6]"
                />
                <span className="min-w-0 flex-1 truncate font-semibold text-gray-700">
                  {resolveAccountName(account)}
                </span>
                {account.currency ? (
                  <span className="text-[11px] font-bold uppercase text-gray-400">{account.currency}</span>
                ) : null}
              </label>
            );
          })
        ) : (
          <div className="px-3 py-4 text-center text-sm font-medium text-gray-400">
            Sin cuentas publicitarias
          </div>
        )}
      </div>
      {selectedAccountIds.length === 1 && !selectedAccountPersisted ? (
        <button
          type="button"
          onClick={onSaveAccount}
          disabled={saving || syncing}
          className="mt-3 inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-[#2b5ce6] px-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#214ec7] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          Guardar cuenta
        </button>
      ) : null}
    </div>
  );
}

function resolveAccountName(account?: FinanceMetaAdAccount | null) {
  if (!account) return "Cuenta publicitaria";
  return account.name || account.account_id || account.id;
}
